// src/pages/AppDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from '../components/Card';
import PrimaryButton from '../components/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton';
import StatusBadge from '../components/StatusBadge';
import ErrorAlert from '../components/ErrorAlert';
import Skeleton, { SkeletonPageTitle } from '../components/Skeleton';
import UptimeHistory from '../components/UptimeHistory';
import { IconChevronRight, IconRefresh, IconExternalLink } from '../icons';
import { formatDate } from '../utils/format';
import { formatCheckInterval, nextCheckAt } from '../utils/checkIntervals';

const STORE_TYPE_LABELS = {
  appstore: 'App Store',
  googleplay: 'Google Play',
  other: 'Other',
};

// The apps history endpoint returns raw periods; map them onto the shape the
// chart expects (label, uptime, counts, barHeight).
const toChartRows = (raw, granularity) =>
  raw.map((item) => {
    let formattedLabel;
    if (granularity === 'day') {
      const [year, month, day] = item.period.split('-');
      formattedLabel = new Date(Date.UTC(year, month - 1, day))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    } else if (granularity === 'hour') {
      formattedLabel = new Date(item.period.replace(' ', 'T') + ':00Z')
        .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    } else {
      formattedLabel = new Date(item.period.replace(' ', 'T') + 'Z')
        .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    }

    return {
      date: formattedLabel,
      fullDate: item.period,
      uptime: item.uptimePercentage,
      totalChecks: item.totalChecks,
      availableCount: item.availableChecks,
      unavailableCount: item.unavailableChecks,
      avgResponseTime: item.avgResponseTime,
      barHeight: item.totalChecks > 0 ? 100 : 1,
    };
  });

const ProxyCard = ({ app, onRetry, retrying }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between gap-2 mb-4">
      <h2 className="text-[15px] font-medium text-ink">Proxy Server</h2>
      {(!app.proxy || app.proxy.isFaulty) && (
        <SecondaryButton onClick={onRetry} loading={retrying}>
          {!retrying && <IconRefresh size={14} />}
          {retrying ? 'Retrying…' : 'Retry proxy'}
        </SecondaryButton>
      )}
    </div>

    {app.proxy ? (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-surface border border-line rounded-lg px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Address</div>
            <div className="text-sm text-ink font-mono font-medium mt-0.5 truncate">
              {app.proxy.host}:{app.proxy.port}
            </div>
          </div>
          <div className="bg-surface border border-line rounded-lg px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Username</div>
            <div className="text-sm text-ink font-mono mt-0.5 truncate">{app.proxy.username}</div>
          </div>
          {app.proxy.location && (
            <div className="bg-surface border border-line rounded-lg px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Location</div>
              <div className="text-sm text-ink mt-0.5 truncate">{app.proxy.location}</div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <StatusBadge
            status={app.proxy.isActive ? 'active' : 'unknown'}
            label={app.proxy.isActive ? 'Active' : 'Inactive'}
          />
          <StatusBadge
            status={app.proxy.isFaulty ? 'faulty' : 'healthy'}
            label={app.proxy.isFaulty ? 'Faulty' : 'Healthy'}
          />
          {app.proxy.errorCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warning-bg text-warning-ink">
              <span className="w-1.5 h-1.5 rounded-full bg-warning-bg0" />
              {app.proxy.errorCount} error{app.proxy.errorCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {app.proxy.lastErrorMessage && (
          <div className="p-3 bg-danger-bg border border-danger-line rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.4px] text-danger/70">Last error</div>
            <div className="text-sm text-danger-ink mt-0.5">{app.proxy.lastErrorMessage}</div>
          </div>
        )}
      </div>
    ) : (
      <div className="text-center py-8">
        <p className="text-[15px] font-medium text-ink">No proxy assigned</p>
        <p className="text-xs text-ink-muted mt-1">This app requires a proxy to perform checks</p>
      </div>
    )}
  </Card>
);

// Скелетон ProxyCard для типичного случая «прокси назначен»: заголовок,
// три плитки Address / Username / Location и ряд бейджей. Подписи плиток
// настоящие — они не зависят от данных.
const ProxyCardSkeleton = () => (
  <Card className="p-4">
    <div className="flex items-center justify-between gap-2 mb-4">
      <h2 className="text-[15px] font-medium text-ink">Proxy Server</h2>
    </div>
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {['Address', 'Username', 'Location'].map((label) => (
          <div key={label} className="bg-surface border border-line rounded-lg px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">{label}</div>
            <div className="h-5 mt-0.5 flex items-center">
              <Skeleton width="60%" height={14} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Skeleton width={72} height={24} radius="full" />
        <Skeleton width={80} height={24} radius="full" />
      </div>
    </div>
  </Card>
);

const AppDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [app, setApp] = useState(null);
  const [error, setError] = useState(null);
  const [checkingNow, setCheckingNow] = useState(false);
  const [retryingProxy, setRetryingProxy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchAppData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/apps/${id}`);
      if (response.data.success) {
        setApp(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching app:', err);
      setError('Failed to load app data');
    }
  }, [id]);

  useEffect(() => { fetchAppData(); }, [fetchAppData]);

  const fetchRows = useCallback(async ({ startDate, endDate, granularity }) => {
    const response = await axios.get(`/api/apps/${id}/aggregated-history`, {
      params: { startDate, endDate, granularity },
    });
    if (!response.data.success) return [];
    return toChartRows(response.data.data.data, granularity);
  }, [id]);

  const handleManualCheck = async () => {
    try {
      setCheckingNow(true);
      await axios.post(`/api/apps/${id}/check`);
      await fetchAppData();
      setReloadToken((t) => t + 1);
    } catch (err) {
      console.error('Error performing manual check:', err);
      setError('Failed to perform availability check');
    } finally {
      setCheckingNow(false);
    }
  };

  const handleRetryProxy = async () => {
    try {
      setRetryingProxy(true);
      await axios.post(`/api/apps/${id}/retry-proxy`);
      await fetchAppData();
    } catch (err) {
      console.error('Error retrying proxy creation:', err);
      setError('Failed to retry proxy creation');
    } finally {
      setRetryingProxy(false);
    }
  };

  // Пока приложение грузится, страница уже стоит в своей раскладке: шапка,
  // прокси и статус — скелетоны, а история грузится параллельно (ей нужен
  // только id).
  const iconSrc = app && (app.iconPath || app.iconUrl);

  const statusCard = (
    <Card className="p-4 flex flex-col w-full">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-[15px] font-medium text-ink">Current Status</h2>
        {app
          ? <StatusBadge status={app.lastCheckStatus} />
          : <Skeleton width={80} height={24} radius="full" />}
      </div>
      <div className="flex-1 flex flex-col justify-center gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Last checked</span>
          {app ? (
            <p className="text-sm font-medium text-ink mt-0.5">{formatDate(app.lastCheckedAt)}</p>
          ) : (
            <div className="h-5 mt-0.5 flex items-center">
              <Skeleton width={140} height={14} />
            </div>
          )}
        </div>
        {app && (
          <div>
            <span className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Check frequency</span>
            <p className="text-sm font-medium text-ink mt-0.5">{formatCheckInterval(app.checkInterval)}</p>
            {nextCheckAt(app) && (
              <p className="text-xs text-ink-muted mt-0.5">
                {nextCheckAt(app) <= new Date()
                  ? 'Next check: due now'
                  : `Next check ≈ ${formatDate(nextCheckAt(app))}`}
              </p>
            )}
          </div>
        )}
        {app?.storeType === 'appstore' && app.lastKnownVersion && (
          <div>
            <span className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Current version</span>
            <p className="text-sm font-medium text-ink mt-0.5">{app.lastKnownVersion}</p>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="bg-page">
      <div className="w-full px-4 sm:px-8 flex flex-col gap-5">
        <button
          onClick={() => navigate('/apps')}
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink transition-colors w-fit"
        >
          <IconChevronRight size={16} className="rotate-180" />
          Back to Apps
        </button>

        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {app ? (
            <div className="flex items-center gap-3 min-w-0">
              {iconSrc && (
                <img
                  src={iconSrc}
                  alt=""
                  className="w-16 h-16 rounded-xl object-cover border border-line shrink-0"
                />
              )}
              <div className="min-w-0">
                <h1 className="text-[22px] leading-8 font-semibold tracking-[-0.44px] text-ink truncate">
                  {app.name}
                </h1>
                {app.description && (
                  <p className="text-xs text-ink-muted truncate">{app.description}</p>
                )}
                <p className="text-xs text-ink-muted mt-0.5 flex items-center gap-1.5">
                  {STORE_TYPE_LABELS[app.storeType]}
                  <span className="text-line">·</span>
                  <a
                    href={app.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-500 hover:underline inline-flex items-center gap-1"
                  >
                    View in store
                    <IconExternalLink size={12} />
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <SkeletonPageTitle withIcon />
          )}
          <PrimaryButton onClick={handleManualCheck} loading={checkingNow} className="shrink-0">
            {!checkingNow && <IconRefresh size={14} />}
            {checkingNow ? 'Checking…' : 'Check now'}
          </PrimaryButton>
        </header>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        {app
          ? <ProxyCard app={app} onRetry={handleRetryProxy} retrying={retryingProxy} />
          : <ProxyCardSkeleton />}

        <UptimeHistory
          title="Availability History"
          metricLabel="Availability"
          okKey="availableCount"
          badKey="unavailableCount"
          okLabel="Available"
          badLabel="Unavailable"
          fetchRows={fetchRows}
          reloadToken={reloadToken}
          sideCard={statusCard}
          onError={setError}
        />
      </div>
    </div>
  );
};

export default AppDetail;
